import { ReactNode } from 'react';

import { BadgeCircle } from '@backrs_ui/common-ui/src/atoms/BadgeCircle';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';

export interface BaseNuggetProps {
  children: ReactNode;
  subText?: string;
  fitContent?: boolean;
  onClick?: () => void;
}

export const BaseNugget = ({ children, subText, fitContent = false, onClick }: BaseNuggetProps) => {
  return (
    <Box sx={containerSx(fitContent)} onClick={onClick}>
      <Box sx={subcontainerSx}>{children}</Box>
      {subText && (
        <Box sx={subTextContainerSx(fitContent)}>
          <Box mt={0.5}>
            <BadgeCircle />
          </Box>
          <Box sx={subTextSx}>
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <Typography component="p" variant="text-sm-regular" sx={{ margin: 0 }}>
                    {children}
                  </Typography>
                ),
              }}
            >
              {subText}
            </ReactMarkdown>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const containerSx =
  (fitContent: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: fitContent ? 'fit-content' : '440px',
    maxWidth: fitContent ? 'unset' : '380px',
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    border: `1px solid ${backrs.colors['Gray/900']}`,
    borderRadius: backrs.radius['radius-xl'],
    overflow: 'hidden',
    cursor: 'pointer',
    ...(fitContent && {
      '@media (min-width: 1300px)': {
        flexDirection: 'row',
        maxWidth: 'unset',
        width: '100%',
      },
    }),
  });

const subcontainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
};

const subTextContainerSx =
  (fitContent: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: backrs.spacing['spacing-md'],
    padding: backrs.spacing['spacing-xl'],
    borderTop: `1px solid ${backrs.colors['Gray/400']}`,
    alignItems: 'flex-start',
    width: '100%',
    borderRadius: backrs.radius['radius-xl'],
    ...(fitContent && {
      '@media (min-width: 1300px)': {
        width: '50%',
        borderLeft: `1px solid ${backrs.colors['Gray/400']}`,
        borderTop: 'none',
        borderRadius: 'unset',
      },
      '@media (min-width: 1450px)': {
        width: '75%',
      },
      '@media (min-width: 1600px)': {
        width: '100%',
      },
    }),
  });

const subTextSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  boxSizing: 'border-box',
  color: backrs.colors['Gray/900'],
  fontStyle: 'italic',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  lineHeight: 1.5,
});
